import type { Reference } from '@medplum/fhirtypes'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
  ResourceOf,
} from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import { extractId, extractVersion } from '@seen/core/utils'
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface UseReadVersionOptions<T> {
  query?: Omit<
    UseQueryOptions<
      T,
      unknown,
      T,
      ReturnType<(typeof cacheKeys)['readExtended']>
    >,
    'initialData' | 'queryKey' | 'queryFn'
  >
}

export function useReadVersion<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  reference: Reference<NoInfer<ResourceOf<T>>>,
  options?: UseReadVersionOptions<T>
): UseQueryResult<T> {
  const medplum = useMedplum()
  const id = extractId(reference) ?? ''
  const versionId = (reference && extractVersion(reference)) ?? ''
  const type = resourceClass.RESOURCE_TYPE
  const className = resourceClass.name

  const result = useQuery<T>({
    ...(options?.query as any),
    enabled:
      Boolean(id) &&
      Boolean(versionId) &&
      (options?.query?.enabled === undefined || options?.query?.enabled),
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.readVersion(type, id, versionId, className),
    queryFn: async () => {
      invariant(id, 'ID is required')
      invariant(versionId, 'Version ID is required')
      return medplum.readVersionClass(resourceClass, id, versionId)
    },
  })

  useErrorToast(
    result.error &&
      `Error accessing ${type}/${id} version ${versionId}: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}
